import React from 'react'
import BackgroundImage from 'features/background-image'
import PageWithSeo from 'features/page-with-seo'
import { Section } from 'layouts'
import { findSectionByIdentifier } from 'lib/pages/api'
import { PageType } from 'models/types/ts'
import PageBySlugQuery from 'queries/page-by-slug'
import { useSelector, useDispatch } from 'react-redux'
import { GlobalState } from 'types'
import { NOT_FOUND } from 'redux-first-router'
import { useQuery } from '@apollo/client'
import getConfig, { isServer } from 'config/web'
import { RESOUCE_SET_BY_NAME } from 'queries/resource-set-by-name/query'
import { pathOr } from 'ramda'
import { BLOCKS, Text } from '@contentful/rich-text-types'
import RichTextType from 'models/types/ts/richTextType'
import Hero from './components/hero'
import PastEvents from './components/past-events'
import CurrentEvents from './components/current-events'

const {
    contentful: { preview },
} = getConfig()

const generateDefaultMessage = (defaultMessage: string): RichTextType => ({
    json: {
        nodeType: BLOCKS.DOCUMENT,
        data: {},
        content: [
            {
                nodeType: BLOCKS.PARAGRAPH,
                data: {},
                content: [
                    {
                        nodeType: 'text',
                        value: defaultMessage,
                        marks: [],
                        data: {},
                    } as Text,
                ],
            },
        ],
    },
    links: {
        assets: {
            block: [],
            hyperlink: [],
        },
        entries: {
            block: [],
            inline: [],
            hyperlink: [],
        },
    },
})

export const defaultTranslations = {
    loginToDonate: generateDefaultMessage('log dafuq in'),
    campaignHasEnded: generateDefaultMessage('Campaign has ended'),
    donationFormDescription: generateDefaultMessage(
        'Choose a fixed PLEX amount or enter your own to support a cause that matters. Every PLEX counts toward creating real-world impact.'
    ),
    enterAmountOf: generateDefaultMessage('Enter amount of '),
    plexBalance: generateDefaultMessage('Plex Balance: '),
    needMorePlex: generateDefaultMessage('Need more PLEX?'),
    selectCharacterPlaceHolder: generateDefaultMessage(
        'Optional: Select character as the donor'
    ),
    donatePlex: generateDefaultMessage('Donate Plex'),
}

export type PlexForGoodTranslations = typeof defaultTranslations

const PlexForGood = (): JSX.Element => {
    const language = useSelector((state: GlobalState) => state.language)
    const dispatch = useDispatch()
    const slug = 'plex-for-good'

    const { loading, error, data } = useQuery(RESOUCE_SET_BY_NAME, {
        variables: { name: 'PlexForGood', locale: language, preview },
        errorPolicy: 'ignore',
        fetchPolicy: isServer ? 'cache-and-network' : 'cache-first',
    })

    const resources = pathOr(
        [],
        ['resourceSetCollection', 'items', '0', 'resourcesCollection', 'items'],
        data
    ).reduce((p, v) => {
        p[v.key] = v.value
        return p
    }, {})

    const translations = Object.keys(defaultTranslations).reduce((p, key) => {
        p[key] = resources[key] || defaultTranslations[key]
        return p
    }, {}) as PlexForGoodTranslations

    return (
        <PageBySlugQuery slug={slug} locale={language}>
            {(page: PageType) => {
                if (page) {
                    const sections = page?.sectionsCollection?.items

                    const fallbackBackground =
                        'https://images.ctfassets.net/7lhcm73ukv5p/2gCNmVCh4xuxQApftg46jE/4787b41fa6ff9bd3a7535ae87b504323/fanfest-hero-22.jpg'
                    const getPageBackground = (): string =>
                        page?.pageBackground
                            ? page.pageBackground.url
                            : fallbackBackground

                    const hero = findSectionByIdentifier(
                        sections,
                        'plex-for-good-hero'
                    )

                    const currentEvent = findSectionByIdentifier(
                        sections,
                        'plex-for-good-current-event'
                    )

                    const everGreen = findSectionByIdentifier(
                        sections,
                        'plex-for-good-ever-green'
                    )

                    const pastEvents = findSectionByIdentifier(
                        sections,
                        'plex-for-good-past-events'
                    )

                    return (
                        <PageWithSeo
                            page={page}
                            showLoading={false}
                            hideSitename={false}
                        >
                            <div>
                                <div>
                                    {hero && (
                                        <Section>
                                            <Hero section={hero} />
                                        </Section>
                                    )}
                                    {(currentEvent || everGreen) && (
                                        <Section>
                                            <CurrentEvents
                                                event={currentEvent}
                                                everGreen={everGreen}
                                                translations={translations}
                                            />
                                        </Section>
                                    )}
                                    {pastEvents && (
                                        <Section>
                                            <PastEvents events={pastEvents} />
                                        </Section>
                                    )}
                                </div>
                            </div>
                        </PageWithSeo>
                    )
                }

                // page not found in contentful
                dispatch({ type: NOT_FOUND })
                return <></>
            }}
        </PageBySlugQuery>
    )
}

export default PlexForGood
